import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

// ---------------------------------------------------------------------------
// UI kit ของ LevelUpCRM — ใช้ร่วมกันทั้งฝั่ง /admin และ /app
// ---------------------------------------------------------------------------

// --------------------------------------------------------------------------
// formatters
// --------------------------------------------------------------------------
export const money = (value, digits = 2) =>
  Number(value || 0).toLocaleString("th-TH", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const baht = (value, digits = 2) => `฿${money(value, digits)}`;

export const compactMoney = (value) => {
  const num = Number(value || 0);
  if (Math.abs(num) >= 1000000) return `฿${(num / 1000000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `฿${(num / 1000).toFixed(1)}K`;
  return `฿${money(num, 0)}`;
};

export const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const date = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  if (!withTime) return date;
  return `${date} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const formatTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const relativeDay = (value) => {
  const diff = Math.round((new Date(value) - new Date()) / 86400000);
  if (diff === 0) return "วันนี้";
  if (diff === 1) return "พรุ่งนี้";
  if (diff === -1) return "เมื่อวาน";
  return diff > 0 ? `อีก ${diff} วัน` : `${Math.abs(diff)} วันก่อน`;
};

export const initialOf = (name) => (name || "?").trim().charAt(0).toUpperCase();

// --------------------------------------------------------------------------
// basics
// --------------------------------------------------------------------------
export const Button = ({ variant = "ghost", size, block, className = "", children, ...rest }) => (
  <button
    type="button"
    className={`btn-crm btn-${variant}-crm ${size === "sm" ? "btn-sm-crm" : ""} ${block ? "btn-block-crm" : ""} ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const Card = ({ title, subtitle, actions, flush, className = "", children, ...rest }) => (
  <section className={`card-crm ${flush ? "card-crm--flush" : ""} ${className}`} {...rest}>
    {title || actions ? (
      <div className="card-crm__head" style={flush ? { padding: "16px 16px 0" } : undefined}>
        <div>
          {title ? <h3>{title}</h3> : null}
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        {actions ? <div className="card-crm__actions">{actions}</div> : null}
      </div>
    ) : null}
    {children}
  </section>
);

export const PageHead = ({ title, subtitle, actions }) => (
  <div className="crm-page-head">
    <div>
      <h1>{title}</h1>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
    {actions ? <div className="crm-page-head__actions">{actions}</div> : null}
  </div>
);

export const StatCard = ({ label, value, hint, trend, icon, tone }) => (
  <div className="stat-card">
    <div className="stat-card__label">
      {icon ? <span>{icon}</span> : null}
      {label}
    </div>
    <div className="stat-card__value" style={tone ? { color: tone } : undefined}>
      {value}
    </div>
    {hint ? <div className="stat-card__hint">{hint}</div> : null}
    {trend ? <div className={`stat-card__trend ${trend.dir}`}>{trend.dir === "up" ? "▲" : "▼"} {trend.text}</div> : null}
  </div>
);

export const Badge = ({ tone = "gray", children }) => <span className={`badge-crm badge-${tone}`}>{children}</span>;

// ป้าย "ยังไม่เชื่อมต่อ" ใช้ซ้ำทุกจุดที่ยังไม่มีบริการภายนอกรองรับ (อย่าแกล้งว่าทำงานได้)
export const NotConnected = ({ label, hint }) => (
  <span className="u-fs-12" title={hint || undefined}>
    {label ? <span className="u-muted">{label}: </span> : null}
    <Badge tone="gray">ยังไม่เชื่อมต่อ</Badge>
  </span>
);

const STATUS_TONE = {
  paid: ["green", "ชำระแล้ว"],
  success: ["green", "สำเร็จ"],
  active: ["green", "ใช้งาน"],
  done: ["green", "เสร็จสิ้น"],
  completed: ["green", "เสร็จสิ้น"],
  confirmed: ["green", "ยืนยันแล้ว"],
  verified: ["green", "ตรวจสอบผ่าน"],
  delivered: ["green", "จัดส่งแล้ว"],
  approved: ["green", "อนุมัติแล้ว"],
  issued: ["green", "ออกแล้ว"],
  won: ["green", "ปิดการขายได้"],
  available: ["green", "ว่าง"],

  pending: ["amber", "รอดำเนินการ"],
  unpaid: ["amber", "ยังไม่ชำระ"],
  waiting_sign: ["amber", "รอลงนาม"],
  scheduled: ["amber", "ตั้งเวลาไว้"],
  pending_rd: ["amber", "รอส่งสรรพากร"],
  packing: ["amber", "กำลังแพ็ก"],
  in_progress: ["amber", "กำลังดำเนินการ"],
  assigned: ["sky", "มอบหมายแล้ว"],
  shipped: ["sky", "ส่งออกแล้ว"],
  sent: ["sky", "ส่งแล้ว"],
  busy: ["sky", "ไม่ว่าง"],
  contacted: ["sky", "ติดต่อแล้ว"],
  qualified: ["indigo", "ผ่านการคัดกรอง"],
  negotiating: ["violet", "กำลังเจรจา"],
  new: ["indigo", "ใหม่"],
  open: ["indigo", "เปิดอยู่"],

  overdue: ["red", "เกินกำหนด"],
  failed: ["red", "ไม่สำเร็จ"],
  rejected: ["red", "ไม่ผ่าน"],
  cancelled: ["red", "ยกเลิก"],
  terminated: ["red", "ยกเลิกสัญญา"],
  noshow: ["red", "ไม่มาตามนัด"],
  lost: ["red", "เสียโอกาส"],
  maintenance: ["amber", "ซ่อมบำรุง"],

  draft: ["gray", "ร่าง"],
  expired: ["gray", "หมดอายุ"],
  inactive: ["gray", "ปิดใช้งาน"],
  closed: ["gray", "ปิดแล้ว"],
};

export const StatusBadge = ({ status, label }) => {
  const [tone, text] = STATUS_TONE[status] || ["gray", status || "-"];
  return <Badge tone={tone}>{label || text}</Badge>;
};

export const Avatar = ({ name, src, size }) => (
  <span className={`avatar-crm ${size === "sm" ? "avatar-crm--sm" : size === "lg" ? "avatar-crm--lg" : ""}`}>
    {src ? <img src={src} alt="" /> : initialOf(name)}
  </span>
);

export const PersonCell = ({ name, sub, src }) => (
  <div className="person-cell">
    <Avatar name={name} src={src} size="sm" />
    <div style={{ minWidth: 0 }}>
      <div className="person-cell__name">{name}</div>
      {sub ? <div className="person-cell__sub">{sub}</div> : null}
    </div>
  </div>
);

// --------------------------------------------------------------------------
// form
// --------------------------------------------------------------------------
export const Field = ({ label, hint, error, children }) => (
  <div className="field-crm">
    {label ? <label>{label}</label> : null}
    {children}
    {hint ? <div className="field-hint">{hint}</div> : null}
    {error ? <div className="field-error">{error}</div> : null}
  </div>
);

export const Input = ({ label, hint, error, ...rest }) => (
  <Field label={label} hint={hint} error={error}>
    <input className="input-crm" {...rest} />
  </Field>
);

export const PhoneInput = ({ label, value, onChange, ...rest }) => (
  <Field label={label}>
    <input
      className="input-crm"
      type="tel"
      inputMode="numeric"
      maxLength={10}
      placeholder="กรอกเบอร์โทรศัพท์ 10 หลัก"
      value={value || ""}
      onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 10))}
      {...rest}
    />
  </Field>
);

export const Textarea = ({ label, hint, ...rest }) => (
  <Field label={label} hint={hint}>
    <textarea className="textarea-crm" {...rest} />
  </Field>
);

export const Select = ({ label, options = [], placeholder, hint, ...rest }) => (
  <Field label={label} hint={hint}>
    <select className="select-crm" {...rest}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => {
        const value = option.value ?? option;
        const text = option.label ?? option;
        return (
          <option key={String(value)} value={value}>
            {text}
          </option>
        );
      })}
    </select>
  </Field>
);

export const Switch = ({ checked, onChange, label }) => (
  <div className="u-between">
    {label ? <span>{label}</span> : null}
    <button type="button" className={`switch-crm ${checked ? "is-on" : ""}`} onClick={() => onChange(!checked)} aria-pressed={checked} />
  </div>
);

export const Qty = ({ value, onChange, min = 1, max = 999 }) => (
  <span className="qty-crm">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))}>−</button>
    <span>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
  </span>
);

// --------------------------------------------------------------------------
// feedback
// --------------------------------------------------------------------------
export const EmptyState = ({ icon = <Icon name="file" />, text = "ยังไม่มีข้อมูล", action }) => (
  <div className="empty-crm">
    <span className="empty-crm__icon">{icon}</span>
    <div>{text}</div>
    {action ? <div className="u-mt-16">{action}</div> : null}
  </div>
);

export const Skeleton = ({ height = 90, className = "", style }) => (
  <div className={`skeleton-crm ${className}`} style={{ height, ...style }} />
);

export const SkeletonRows = ({ count = 5, height = 44 }) => (
  <div style={{ padding: 12, display: "grid", gap: 8 }}>
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton key={index} height={height} />
    ))}
  </div>
);

export const ProgressBar = ({ percent, color }) => (
  <div className="progress-crm">
    <div className="progress-crm__fill" style={{ width: `${Math.min(100, Math.max(0, percent))}%`, background: color }} />
  </div>
);

export const Modal = ({ open, title, size, onClose, footer, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  // render ผ่าน portal ไป document.body — กัน position:fixed หลุดกรอบเวลาบรรพบุรุษมี
  // animation ที่แตะ transform ค้างอยู่ (สร้าง containing block ใหม่โดยไม่ตั้งใจ)
  return createPortal(
    <div className="modal-backdrop-crm" onClick={onClose}>
      <div
        className={`modal-crm ${size === "lg" ? "modal-crm--lg" : size === "sm" ? "modal-crm--sm" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-crm__head">
          <h3>{title}</h3>
          <button type="button" className="modal-crm__close" onClick={onClose}>×</button>
        </div>
        <div className="modal-crm__body">{children}</div>
        {footer ? <div className="modal-crm__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
};

export const Drawer = ({ open, title, onClose, children, footer }) => {
  if (!open) return null;
  return createPortal(
    <>
      <div className="modal-backdrop-crm" style={{ justifyContent: "flex-end", padding: 0 }} onClick={onClose} />
      <aside className="drawer-crm">
        <div className="modal-crm__head">
          <h3>{title}</h3>
          <button type="button" className="modal-crm__close" onClick={onClose}>×</button>
        </div>
        <div className="modal-crm__body" style={{ flex: "1 1 auto", overflowY: "auto" }}>{children}</div>
        {footer ? <div className="modal-crm__foot">{footer}</div> : null}
      </aside>
    </>,
    document.body
  );
};

export const Tabs = ({ tabs = [], value, onChange }) => (
  <div className="tabs-crm">
    {tabs.map((tab) => (
      <button
        key={tab.key}
        type="button"
        className={value === tab.key ? "is-active" : ""}
        onClick={() => onChange(tab.key)}
      >
        {tab.label}
        {tab.count !== undefined ? ` (${tab.count})` : ""}
      </button>
    ))}
  </div>
);

export const Timeline = ({ items = [] }) => (
  <div className="timeline-crm">
    {items.map((item) => (
      <div className="timeline-item" key={item.id}>
        <div className="u-between">
          <strong>{item.title}</strong>
          <span className="u-fs-12 u-muted">{formatDate(item.createdAt, true)}</span>
        </div>
        <div className="u-fs-13 u-muted">{item.detail}</div>
        {item.amount ? <div className="u-fs-13 u-strong">{baht(item.amount)}</div> : null}
      </div>
    ))}
    {items.length === 0 ? <EmptyState text="ยังไม่มีความเคลื่อนไหว" /> : null}
  </div>
);

// --------------------------------------------------------------------------
// data table
// --------------------------------------------------------------------------
export const Pagination = ({ page, perPage, total, onPage, onPerPage }) => {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  const maxPage = Math.max(1, Math.ceil(total / perPage));
  return (
    <div className="table-pagination">
      {onPerPage ? (
        <span>
          แถวต่อหน้า
          <select className="select-crm" style={{ width: "auto", marginLeft: 6, padding: "3px 6px" }} value={perPage} onChange={(e) => onPerPage(Number(e.target.value))}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </span>
      ) : null}
      <span>{start}-{end} จาก {total}</span>
      <span className="u-flex u-gap-4">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
        <button type="button" disabled={page >= maxPage} onClick={() => onPage(page + 1)}>›</button>
      </span>
    </div>
  );
};

/**
 * columns = [{ key, label, width, align, sortable, render(row, index) }]
 */
export const DataTable = ({
  columns = [],
  rows = [],
  total,
  page = 1,
  perPage = 10,
  loading,
  onPage,
  onPerPage,
  onSort,
  sortBy,
  sortDir,
  onRowClick,
  emptyText = "ยังไม่มีข้อมูล",
}) => (
  <div className="card-crm card-crm--flush">
    <div className="table-wrap">
      <table className="table-crm">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.sortable ? "is-sortable" : ""}
                style={{ width: column.width, textAlign: column.align }}
                onClick={() => column.sortable && onSort?.(column.key)}
              >
                {column.label}
                {sortBy === column.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loading &&
            rows.map((row, index) => (
              <tr
                key={row.id || index}
                className={onRowClick ? "is-clickable" : ""}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => (
                  <td key={column.key} style={{ textAlign: column.align }}>
                    {column.render ? column.render(row, index) : row[column.key] ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>

    {loading ? <SkeletonRows /> : null}
    {!loading && rows.length === 0 ? <EmptyState text={emptyText} /> : null}
    {onPage ? <Pagination page={page} perPage={perPage} total={total ?? rows.length} onPage={onPage} onPerPage={onPerPage} /> : null}
  </div>
);

// --------------------------------------------------------------------------
// kanban (drag & drop ด้วย HTML5 native)
// --------------------------------------------------------------------------
export const Kanban = ({ columns = [], items = [], groupBy = "stage", renderCard, onMove }) => {
  const [overKey, setOverKey] = useState(null);

  return (
    <div className="kanban">
      {columns.map((column) => {
        const columnItems = items.filter((item) => item[groupBy] === column.key);
        return (
          <div
            key={column.key}
            className={`kanban__col ${overKey === column.key ? "is-over" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setOverKey(column.key);
            }}
            onDragLeave={() => setOverKey(null)}
            onDrop={(event) => {
              event.preventDefault();
              setOverKey(null);
              const id = event.dataTransfer.getData("text/plain");
              if (id) onMove?.(id, column.key);
            }}
          >
            <div className="kanban__col-head">
              <span>{column.label}</span>
              <span className="kanban__count">{columnItems.length}</span>
            </div>
            {columnItems.map((item) => (
              <div
                key={item.id}
                className="kanban__card"
                draggable
                onDragStart={(event) => event.dataTransfer.setData("text/plain", item.id)}
              >
                {renderCard(item)}
              </div>
            ))}
            {columnItems.length === 0 ? <div className="u-fs-12 u-muted u-center" style={{ padding: 14 }}>ลากการ์ดมาที่นี่</div> : null}
          </div>
        );
      })}
    </div>
  );
};

// --------------------------------------------------------------------------
// upload + signature
// --------------------------------------------------------------------------
export const UploadBox = ({ onFile, accept = "image/*", hint = "คลิกเพื่อเลือกไฟล์ หรือ ลากไฟล์มาวาง", preview }) => {
  const inputRef = useRef(null);
  return (
    <div>
      <div
        className="upload-box"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const file = event.dataTransfer.files?.[0];
          if (file) onFile(file);
        }}
      >
        {preview ? (
          <img src={preview} alt="preview" style={{ maxHeight: 200, maxWidth: "100%", borderRadius: 10 }} />
        ) : (
          <>
            <div style={{ fontSize: 30 }}><Icon name="upload" /></div>
            <div className="u-fs-13 u-muted u-mt-8">{hint}</div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
        }}
      />
    </div>
  );
};

export const SignaturePad = ({ onChange, height = 180 }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const point = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = event.touches?.[0];
    return {
      x: ((touch ? touch.clientX : event.clientX) - rect.left) * (canvas.width / rect.width),
      y: ((touch ? touch.clientY : event.clientY) - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (event) => {
    event.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(event);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="sign-pad"
        width={760}
        height={height * 2}
        style={{ height }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="u-mt-8">
        <Button size="sm" onClick={clear}>ล้างลายเซ็น</Button>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// hooks
// --------------------------------------------------------------------------
export const useAsync = (fn, deps = [], initial = null) => {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.resolve(fn())
      .then((result) => {
        if (!alive) return;
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);

  return { data, loading, reload: () => setReloadKey((k) => k + 1), setData };
};

export const useTableState = (initialPerPage = 10) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const onSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  return useMemo(
    () => ({
      page,
      perPage,
      search,
      sortBy,
      sortDir,
      setPage,
      setPerPage: (value) => {
        setPerPage(value);
        setPage(1);
      },
      setSearch: (value) => {
        setSearch(value);
        setPage(1);
      },
      onSort,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, perPage, search, sortBy, sortDir]
  );
};
